import AppBar from "./AppBar";
import { Trans, useTranslation } from "react-i18next";
import { useState, ChangeEvent, Fragment, useEffect, useMemo, useRef } from 'react';
import { useSnackbar } from "notistack";
import { uploadAddressfile, bulkAddressUsage, getBulkProcessedFiles } from "../address/data4homeAPI"
import { useAppSelector } from "../../app/hooks";
import { selectUserInfo } from "../userInfo/userInfoSlice";
import { selectActiveTenantId } from "../activeTenant/activeTenantSlice";
import CSVParser from 'papaparse';
import * as XLSX from 'xlsx';
import { filesize } from "filesize";
import moment from "moment";
import { SUPPORT_EMAIL } from "../../constants";
import Box from "@mui/material/Box";
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Modal from '@mui/material/Modal';
import HelpIcon from '@mui/icons-material/Help';
import CssBaseline from "@mui/material/CssBaseline";
import Checkbox from "@mui/material/Checkbox";
import Link from "@mui/material/Link";
import { styled } from '@mui/material/styles';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import { LineChart } from '@mui/x-charts/LineChart';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import MuiListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import IconButton from '@mui/material/IconButton';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FolderIcon from '@mui/icons-material/Folder';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import DownloadIcon from '@mui/icons-material/Download';
import Divider from '@mui/material/Divider';
import Fab from '@mui/material/Fab';
// import Box from '@mui/material/Box';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepContent from '@mui/material/StepContent';
import StepButton from '@mui/material/StepButton';
import CircularProgress from '@mui/material/CircularProgress';
// import Button from '@mui/material/Button';
// import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import FormControlLabel from '@mui/material/FormControlLabel';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';

const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});

const ListItem = styled('li')(({ theme }) => ({
  margin: theme.spacing(0.5),
}));

const BULK_PDF_RESOURCE = "vision_data4home-adress_bulk_pdf";
const PDF_ROW_CAP_CODES = ["BULK_PDF_MAX_ROWS", "pdfRowCapExceeded"];

function countBulkAddressRows(rows: any[] | undefined, withHeader: boolean): number {
  if (!rows || rows.length === 0) {
    return 0;
  }
  return withHeader ? Math.max(rows.length - 1, 0) : rows.length;
}

function hasBulkPdfPermission(userInfo: { value?: { rtp?: any[] } }): boolean {
  const rtp = userInfo.value?.rtp;
  if (!Array.isArray(rtp)) {
    return false;
  }
  return rtp.some((item: any) => item?.resource === BULK_PDF_RESOURCE && item?.scope?.includes("view"));
}

async function readErrorBody(data: unknown): Promise<any> {
  if (data == null) {
    return {};
  }
  if (typeof data === "string") {
    return parseErrorJson(data);
  }
  if (typeof Blob !== "undefined" && data instanceof Blob) {
    return parseErrorJson(await data.text());
  }
  if (data instanceof ArrayBuffer) {
    return parseErrorJson(new TextDecoder().decode(data));
  }
  if (typeof data === "object") {
    return data;
  }
  return {};
}

function parseErrorJson(text: string): any {
  try {
    return JSON.parse(text);
  } catch {
    return {};
  }
}

function bulkUploadErrorMessage(content: any, translate: (key: string) => string): string {
  const apiMessage = typeof content?.error === "string" && content.error.trim()
    ? content.error.trim()
    : typeof content?.message === "string" && content.message.trim()
      ? content.message.trim()
      : "";
  const code = content?.code || content?.errorCode;
  const isRowCap = PDF_ROW_CAP_CODES.includes(code)
    || content?.pdfRowCap === true
    || /BULK_PDF_MAX_ROWS|pdfRowCapExceeded/i.test(apiMessage);
  if (isRowCap) {
    return apiMessage || translate("addressBulk.pdfRowCapExceeded");
  }
  return apiMessage || translate("addressBulk.uploadFileFailed");
}

function ChipContent({ params, onchange }: any) {
  const { t } = useTranslation();
  const handleDistanceChange = (value: string) => {
    onchange({
      distance: value,
      unit: params.unit,
    });
  }

  const handleUnitChange = (value: string) => {
    onchange({
      distance: params.distance,
      unit: value,
    });
  }

  //TODO: input field validation

  return (
    <Fragment>
      <span>{params.title}</span>
      {
        params.unit
        ? (
          <Fragment>
            <input
              type="number"
              style={{
                border: "1px solid #CCCCCC",
                borderRadius: 4,
                paddingLeft: 8,
                width: 50
              }}
              placeholder="distance en mètre"
              value={params.distance}
              onChange={(e) => handleDistanceChange(e.target.value)}
            />
            <select value={params.unit} onChange={(e) => handleUnitChange(e.target.value)}>
              <option value="m">{t("m")}</option>
              <option value="km">{t("km")}</option>
            </select>
          </Fragment>
        )
        : null
      }
    </Fragment>
  )
}

export default function BulkAddressPage() {
  const { t } = useTranslation();
  const userInfo = useAppSelector(selectUserInfo);
  const canGenerateBulkPdf = hasBulkPdfPermission(userInfo);
  const [file, setFile] = useState<File | null>(null);
  const [generatePdf, setGeneratePdf] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const activeTenantId = useAppSelector(selectActiveTenantId);
  const [columnsSelection, setColumnsSelection] = useState<any>({available: [], selected: []});
  const [headerColumnsSelection, setHeaderColumnsSelection] = useState<any>({available: [], selected: []});
  const [fileContent, setFileContent] = useState<{ type: 'csv' | 'xlsx', data: string | string[][] } | null>(null);
  const [hasHeader, setHasHeader] = useState<boolean>(false);
  const [requestLoading, setRequestLoading] = useState<boolean>(false);
  const [usageRequestLoading, setUsageRequestLoading] = useState<boolean>(false);
  const [usage, setUsage] = useState<any>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [activeStep, setActiveStep] = useState(0);
  const [chartData, setChartData] = useState<any>({xAxis: [], series: []});
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [processedFiles, setProcessedFiles] = useState<any[]>([]);
  const [filesLoading, setFilesLoading] = useState<boolean>(false);
  const drawerWidth = 300;
  // const hasAddresssBulkFeature = userInfo.value && userInfo.value.groups && userInfo.value.groups.includes("/features/address_bulk");

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleStep = (step: number) => () => {
    setActiveStep(step);
  };

  // const handleReset = () => {
  //   setActiveStep(0);
  // };

  const reportDataParameters = useMemo(function() {
    return [
      {
        title: 'surface du batîment au sol (m2)',
        id: 'geoshapeComputedArea',
        selected: true
      },
      {
        title: 'hauteur du batîment (en mètre)',
        id: 'maxHeight',
        selected: true
      },
      {
        title: 'nombre d\'étages estimé',
        id: 'estimatedFloorCount',
        selected: true
      },
      {
        title: 'surface globale estimée (m2)',
        id: 'livingAreaEstimation',
        selected: true
      },
      {
        title: 'année de contruction',
        id: 'constructionYear',
        selected: true
      },
      {
        title: 'matériaux murs et toiture',
        id: 'buildingMaterials',
        selected: true
      },
      {
        title: 'altitude (en metre au dessus du niveau de la mer)',
        id: 'altitude',
        selected: true
      },
      // TODO: the following columns are for water ways but 2 of them have distance, which one will be used?
      // and we cannot add all of them because we will have the same problem.
      // we should handle adding water ways columns as one piece of data, I think
      {
        id: 'waterWayName',
        title: 'Cours d\eau le plus proche dans les ',
        selected: true,
        distance: 5,
        unit: "km",
      },
      {
        id: 'waterWayDistance',
        title: 'Distance à vol d\'oiseau avec le cours d\'eau dans les ',
        selected: true,
        distance: 5,
        unit: "km",
      },
      {
        id: 'waterWayAltitude',
        title: 'Altitude normale (hors crue) du cours d\'eau le plus proche',
        selected: true
      },
      {
        id: 'waterWayDifference',
        title: 'Différence entre la hauteur à l\'adresse et le niveau du cours d\'eau le plus proche',
        selected: true
      },
      {
        id: 'railRoadDistance',
        title: 'Distance à vol d\'oiseau avec la voie ferrée la plus proche dans les ',
        selected: true,
        distance: 5,
        unit: "km",
      },
      {
        id: 'treesNearby',
        title: 'Présence d\'arbres à proximité du bâtiment dans les ',
        selected: true,
        distance: 15,
        unit: "m",
      },
      {
        id: 'fireStationDistance',
        title: t("fireStation.bulkColumn"),
        selected: true,
      },
      {
        title: 'zones surveillées',
        id: 'zonesSurveillees',
        selected: true
      },
      // {
      //   title: 'Batiment dans un quartier prioritaire de la ville',
      //   id: 'zoneQpv',
      //   selected: true
      // },
      // {
      //   title: 'Batiment dans un quartier prioritaire ancien ou dégradé',
      //   id: 'zoneQpvAncienDegrade',
      //   selected: true
      // },
      // {
      //   title: 'Batiment dans un quartier de veille active',
      //   id: 'zoneQva',
      //   selected: true
      // },
      // {
      //   title: 'Batiment dans une zone urbaine sensible',
      //   id: 'zoneZus',
      //   selected: true
      // },
      // {
      //   title: 'Batiment dans une zone franche urbaine',
      //   id: 'zoneZfu',
      //   selected: true
      // },
      // {
      //   title: 'Batiment dans un quartier du NPNRU',
      //   id: 'zoneNpnru',
      //   selected: true
      // },
      {
        title: 'manifestation',
        id: 'manifestation',
        selected: true
      },
      {
        title: 'monuments historiques dans les ',
        id: 'historicMonuments',
        selected: true,
        distance: 500,
        unit: "m",
      },
      {
        title: 'nombre de site seveso dans les ',
        id: 'seveso',
        selected: true,
        distance: 20,
        unit: "km",
      },
      {
        title: 'risque de crue',
        id: 'flood',
        selected: true
      },
      {
        title: 'risque de sécheresse',
        id: 'drought',
        selected: true
      },
      {
        title: 'risque d\'incendies',
        id: 'fire',
        selected: true
      },
      {
        title: 'risque de grêle',
        id: 'hail',
        selected: true
      },
      {
        title: 'risque de fortes pluie',
        id: 'heavyRain',
        selected: true
      },
      {
        title: 'risque de vents violents',
        id: 'wind',
        selected: true
      },
      {
        title: 'risque de submersion',
        id: 'submersion',
        selected: true
      },
      {
        title: 'risque d\' érosion côtière',
        id: 'coastalErosion',
        selected: true
      },
      {
        title: 'présence de catnat pour chaque risque',
        id: 'catnat',
        selected: true
      },
      {
        title: 'présence de PPRn pour chaque risque',
        id: 'pprn',
        selected: true
      },
      {
        title: 'arrêtés de péril',
        id: 'perils',
        selected: true
      },
    ]}, [t]
  );

  const getUsage = async function() {
    setUsageRequestLoading(true);
    const usage = await bulkAddressUsage();
    setUsage(usage);
    setUsageRequestLoading(false);
  }

  const getProcessedFiles = async function() {
    setFilesLoading(true);
    try {
      const files = await getBulkProcessedFiles();
      setProcessedFiles(files || []);
    } catch (error: any) {
      console.error('Error fetching processed files:', error);
      setProcessedFiles([]);
      // Afficher un message d'erreur à l'utilisateur
      const errorMessage = error?.response?.data?.error || error?.message || t('addressBulk.fetchFilesError');
      enqueueSnackbar(
        errorMessage,
        { variant: "error" }
      );
    } finally {
      setFilesLoading(false);
    }
  }

  const handleDownloadFile = (fileUrl: string, fileName: string) => {
    const link = document.createElement('a');
    link.href = fileUrl;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  const handleDrawerToggle = () => {
    const newDrawerOpen = !drawerOpen;
    setDrawerOpen(newDrawerOpen);
    
    // Mettre à jour le hash de l'URL
    if (newDrawerOpen) {
      window.location.hash = 'files';
    } else {
      // Si le hash est #files, on l'enlève
      if (window.location.hash === '#files') {
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
      }
    }
  }

  const monthNames = useMemo(function() {
    return [
      "Jan",
      "Fév",
      "Mar",
      "Avr",
      "Mai",
      "Juin",
      "Juil",
      "Aoû",
      "Sep",
      "Oct",
      "Nov",
      "Déc",
    ];
  }, []);

  useEffect(function() {
    if (usage) {
      let data = usage.perMonth.perMonth.sort(function(a: any, b: any) { return a.year - b.year || a.month - b.month; });
      for (let i = 0; i < 12; i++) {
        if (data[i] && data[i].month !== i + 1) {
          data.splice(i, 0, {
            year: moment().year(),
            month: i + 1,
            usage: 0,
          });
        }
      }
      setChartData({
        xAxis: [{scaleType: "band", data: data.map(function(item: any) { return monthNames[item.month - 1]; })}],
        series: [{id: "usage", curve: "linear", data: data.map(function(item: any) { return item.usage; }), area: true}],
      });
    }
  }, [usage])

  // Vérifier le hash au chargement initial et écouter les changements de hash
  useEffect(function() {
    // Vérifier le hash au chargement initial
    const hash = window.location.hash;
    if (hash === '#files') {
      setDrawerOpen(true);
    }

    // Écouter les changements de hash (bouton retour du navigateur)
    const handleHashChange = () => {
      const currentHash = window.location.hash;
      if (currentHash === '#files') {
        setDrawerOpen(true);
      } else {
        setDrawerOpen(false);
      }
    };

    window.addEventListener('hashchange', handleHashChange);

    return function cleanup() {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  // Exécuter la requête à intervalle régulier quand le drawer est ouvert
  useEffect(function() {
    if (!drawerOpen) {
      return;
    }

    // Exécuter immédiatement
    getProcessedFiles();

    // Puis exécuter toutes les minutes
    const interval = setInterval(function() {
      getProcessedFiles();
    }, 60000); // 1 minute = 60000 ms

    // Nettoyer l'intervalle quand le drawer se ferme ou le composant se démonte
    // Cette fonction de cleanup est appelée automatiquement par React quand :
    // - drawerOpen change (drawer se ferme)
    // - Le composant est démonté (unmount)
    return function cleanup() {
      clearInterval(interval);
    };
  }, [drawerOpen])

  useEffect(function() {
    const selectedColumns = reportDataParameters.slice();
    const availableColumns = reportDataParameters.filter(function(item) {
      const selectedColumnIds = selectedColumns.map(function(selectedColumn) { return selectedColumn.id; });
      return !selectedColumnIds.includes(item.id)
    });
    setColumnsSelection({
      selected: selectedColumns,
      available: availableColumns
    });

    getUsage();
    // getProcessedFiles sera appelé automatiquement par l'intervalle si le drawer est ouvert

  }, [reportDataParameters])

  const handleEventFileChange = (event: ChangeEvent<HTMLInputElement>): void => {
      if (event.target.files) {
        if (event.target.files.length === 0) {
          setFile(null);
          setFileContent(null);
          setActiveStep(0);
          enqueueSnackbar(t('addressBulk.noFileSelected'), { variant: "error" });
          return;
        }

        const file = event.target.files[0];
        // TODO: validate type
        setFile(file);
        
        const fileExtension = file.name.split('.').pop()?.toLowerCase();
        
        if (fileExtension === 'xlsx' || fileExtension === 'xls') {
          // Lire le fichier XLSX
          const reader = new FileReader();
          reader.onload = (evt) => {
            const data = evt.target?.result;
            if (!data) {
              enqueueSnackbar(t('addressBulk.failedToReadAddressFileContent'));
              return;
            }
            try {
              const workbook = XLSX.read(data, { type: 'array' });
              const firstSheetName = workbook.SheetNames[0];
              const worksheet = workbook.Sheets[firstSheetName];
              // Convertir en format CSV-like (tableau de tableaux)
              const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });
              // Stocker les données parsées avec le type
              setFileContent({ type: 'xlsx', data: jsonData as string[][] });
            } catch (error) {
              console.error('Error reading XLSX file:', error);
              enqueueSnackbar(t('addressBulk.failedToReadAddressFileContent'));
            }
          };
          reader.readAsArrayBuffer(file);
        } else {
          // Lire le fichier CSV (code existant)
          const reader = new FileReader();
          reader.onload = (evt) => {
            const content = evt.target?.result as string;
            if (!content) {
              enqueueSnackbar(t('addressBulk.failedToReadAddressFileContent'));
              return;
            }
            setFileContent({ type: 'csv', data: content });
          };
          reader.readAsText(file);
        }
      }
  };

  // TODO: validate file structure and try to geocode address (limit to 100 addresses)
  // TODO: test data.gouv geocoding api

  const handleEventFileSubmit = (): void => {
      // TODO: validate form parameters

      if (!file) {
          alert('Veuillez sélectionner un fichier à télécharger.');
          return;
      }

      const tenantId = activeTenantId ?? userInfo.value?.tenants?.[0]?.tenantId;

      const formData = new FormData();
      if (tenantId) {
          formData.append("tenantId", tenantId);
      }
      formData.append('file', file);
      formData.append('columns', columnsSelection.selected.map(function(column: any) {
        return column.id
      }));
      formData.append('hasHeader', JSON.stringify(hasHeader));
      formData.append('address_columns', headerColumnsSelection.selected.map(function(column: any) {
        return column.id
      }));
      const distanceColumns = columnsSelection.selected.filter(function(column: any) {
        return column.distance !== undefined;
      });

      for (let column of distanceColumns) {
        formData.append(`${column.id}-distance`, `${column.distance}-${column.unit}`);
      }

      const pdfMaxRows = typeof usage?.pdfMaxRows === "number" ? usage.pdfMaxRows : null;
      const addressRowCount = countBulkAddressRows(csvContent, hasHeader);
      if (canGenerateBulkPdf && generatePdf && pdfMaxRows != null && addressRowCount > pdfMaxRows) {
        enqueueSnackbar(
          t("addressBulk.generatePdfFileTooLarge", { count: addressRowCount, max: pdfMaxRows }),
          { variant: "error" },
        );
        return;
      }

      if (canGenerateBulkPdf && generatePdf) {
        formData.append('generatePdf', 'true');
      }

      setRequestLoading(true);
      uploadAddressfile(formData)
      .then(data => {

        // // Créer un lien pour le téléchargement
        // const urlBlob = window.URL.createObjectURL(data);
        // const a = document.createElement('a');
        // a.href = urlBlob;
        // a.download = `report_${file.name}`; // Remplacez par le nom du fichier que vous voulez
        // document.body.appendChild(a);
        // a.click();

        // // Nettoyer
        // a.remove();
        // window.URL.revokeObjectURL(urlBlob);

        enqueueSnackbar(
            t("addressBulk.uploadFileSuccess"),
            { variant: "success" }
        );

        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        setFile(null);
        setFileContent(null);
        setActiveStep(0);
        getUsage();
        // getProcessedFiles sera appelé automatiquement par l'intervalle si le drawer est ouvert
      })
      .catch(async (error) => {
          if (!error.response) {
            return;
          }
          let message = t("addressBulk.uploadFileFailed");
          try {
            const content = await readErrorBody(error.response.data);
            message = bulkUploadErrorMessage(content, t);
          } catch (exception) {
            // keep the translated fallback when the error body cannot be read
          }
          enqueueSnackbar(
              message,
              { variant: "error" }
          );
      })
      .finally(() => {
        setRequestLoading(false);
      });
  };

  /* report file column selection */

  const handleAdd = (data: any) => () => {
    const newSelectedColumns = reportDataParameters.filter(function(item) {
      const selectedColumnIds = columnsSelection.selected.map(function(selectedColumn: any) { return selectedColumn.id; });
      return selectedColumnIds.includes(item.id) || data.id === item.id;
    });
    const newAvailableColumns = reportDataParameters.filter(function(item) {
      const selectedColumnIds = newSelectedColumns.map(function(selectedColumn) { return selectedColumn.id; });
      return !selectedColumnIds.includes(item.id)
    });

    setColumnsSelection({
      selected: newSelectedColumns,
      available: newAvailableColumns
    });
  }

  const handleDelete = (data: any) => () => {
    const newSelectedColumns = columnsSelection.selected.filter(function(item: any) {
      return item.id !== data.id;
    });
    const newAvailableColumns = reportDataParameters.filter(function(item) {
      const selectedColumnIds = newSelectedColumns.map(function(selectedColumn: any) { return selectedColumn.id; });
      return !selectedColumnIds.includes(item.id)
    });

    setColumnsSelection({
      selected: newSelectedColumns,
      available: newAvailableColumns
    })
  }

  const handleDistanceChange = (data: any) => (value: any) => {
    for (const item of reportDataParameters) {
      if (item.id === data.id) {
        item.distance = value.distance;
        item.unit = value.unit;
      }
    }
    const selectedColumns = reportDataParameters.filter(function(item) {
      const selectedColumnIds = columnsSelection.selected.map(function(selectedColumn: any) { return selectedColumn.id; });
      return selectedColumnIds.includes(item.id)
    });
    const availableColumns = reportDataParameters.filter(function(item) {
      const selectedColumnIds = columnsSelection.selected.map(function(selectedColumn: any) { return selectedColumn.id; });
      return !selectedColumnIds.includes(item.id)
    });
    setColumnsSelection({
      selected: selectedColumns,
      available: availableColumns
    });
  }

  const handleSelectAll = () => {
    const newSelectedColumns = reportDataParameters.filter(function(item) {
      return true;
    });
    const newAvailableColumns = reportDataParameters.filter(function(item) {
      return false;
    });

    setColumnsSelection({
      selected: newSelectedColumns,
      available: newAvailableColumns
    });
  }

  const handleDeselectAll = () => {
    const newSelectedColumns = reportDataParameters.filter(function(item) {
      return false;
    });
    const newAvailableColumns = reportDataParameters.filter(function(item) {
      return true;
    });

    setColumnsSelection({
      selected: newSelectedColumns,
      available: newAvailableColumns
    });
  }

  /* end report file column selection */

  const csvContent = useMemo<any[]|undefined>(function() {
    if (!fileContent) { return []; }

    if (fileContent.type === 'xlsx') {
      // Les données XLSX sont déjà parsées en tableau de tableaux
      return fileContent.data as string[][];
    } else {
      // Parser le CSV
      const csvData = fileContent.data as string;
      const parseResult = CSVParser.parse<string[]>(csvData, { header: false, skipEmptyLines: true });
      return parseResult.data;
    }
  }, [fileContent]);

  const pdfMaxRows = typeof usage?.pdfMaxRows === "number" ? usage.pdfMaxRows : null;
  const addressRowCount = countBulkAddressRows(csvContent, hasHeader);
  const pdfFileExceedsCap = pdfMaxRows != null && addressRowCount > pdfMaxRows;

  useEffect(function() {
    if (pdfFileExceedsCap) {
      setGeneratePdf(false);
    }
  }, [pdfFileExceedsCap]);

  /* header selection */

  const headers = useMemo(function() {
    if (!csvContent || csvContent.length === 0) {
      return [];
    }

    // generate column name sequentially until 703 cols, then break
    const aChar = 65;
    if (!hasHeader) {
      const generatedHeader = [];
      for (let i = 0; i < csvContent[0].length; i++) {
        const charList = [];
        const test = i % 26;
        const test2 = Math.floor(i / 26);
        if (test2 > 0) {
          charList.push(aChar + (test2 - 1));
        }
        charList.push(aChar + test);
        generatedHeader.push({
          title: String.fromCharCode(...charList),
          id: i
        })
      }
      return generatedHeader;
    }

    return csvContent[0].map(function(header: string, index: number) {
      return {
        title: header,
        id: index
      }
    });
  }, [hasHeader, csvContent]);

  useEffect(function() {
    if (csvContent && csvContent.length) {
      setHeaderColumnsSelection({
        selected: [],
        available: headers
      })
    }
  }, [csvContent, headers]);

  const handleHeaderAdd = (data: any) => () => {
    const newSelectedColumns = headers.filter(function(item: any) {
      const selectedColumnIds = headerColumnsSelection.selected.map(function(selectedColumn: any) { return selectedColumn.id; });
      return selectedColumnIds.includes(item.id) || data.id === item.id;
    });
    const newAvailableColumns = headers.filter(function(item: any) {
      const selectedColumnIds = newSelectedColumns.map(function(selectedColumn: any) { return selectedColumn.id; });
      return !selectedColumnIds.includes(item.id)
    });

    setHeaderColumnsSelection({
      selected: newSelectedColumns,
      available: newAvailableColumns
    });
  }

  const handleHeaderDelete = (data: any) => () => {
    const newSelectedColumns = headerColumnsSelection.selected.filter(function(item: any) {
      return item.id !== data.id;
    });
    const newAvailableColumns = headers.filter(function(item: any) {
      const selectedColumnIds = newSelectedColumns.map(function(selectedColumn: any) { return selectedColumn.id; });
      return !selectedColumnIds.includes(item.id)
    });

    setHeaderColumnsSelection({
      selected: newSelectedColumns,
      available: newAvailableColumns
    })
  }

  /* end header selection */

  const inputCSVFileIsValidationErrors = useMemo(function() {
    const errors: any[] = [];
    if (!csvContent || !csvContent.length) {
      errors.push({
        isValid: false,
        reason: 'addressBulk.inputFileValidation.isEmpty',
      });
      return errors;
    }

    // if (!csvContent[0] || csvContent[0][0] !== 'adresse') {
    //   errors.push({
    //     isValid: false,
    //     reason: 'addressBulk.inputFileValidation.theFirstColumnMustBeAddress',
    //   })
    // }

    // if (!csvContent[0] || csvContent[0].length !== 1) {
    //   errors.push({
    //     isValid: false,
    //     reason: 'addressBulk.inputFileValidation.mustContainOnlyOneColumn',
    //   })
    // }

    if (headerColumnsSelection.selected.length === 0) {
      errors.push({
        isValid: false,
        reason: 'addressBulk.inputFileValidation.columnsFormingAddressMustNotBeEmpty',
      })
    }

    if (usage && (csvContent.length - 1 > (usage.limit - (usage.total || 0)))) {
      errors.push({
        isValid: false,
        reason: 'addressBulk.inputFileValidation.rowCountIsGreaterThanUsage',
      })
      return errors;
    }

    return errors;
  }, [csvContent, headerColumnsSelection, usage]);

  const renderCSVFile = function(csvContent: any, headers: any, addAddressColumns: boolean = true) {
    // const rows = fileContent.split(lineSeparator);
    // const csvContent = rows.map(function(line: string) {
    //   const regex = new RegExp(`("?[^${fieldSeparator}]+"?)`, "g")
    //   const match = line.match(regex);
    //   return match;
    // });

    return (
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>

        <TableContainer sx={{ maxHeight: 440 }}>
          <Table sx={{ whiteSpace: 'nowrap' }}>
            <TableHead>
              <TableRow key="csv-header">
                {addAddressColumns ?
                  <TableCell sx={{ fontWeight: 'bold' }} key={`csv-address-line-header`}>
                    {t('addressBulk.addressColumnHeader')}
                  </TableCell>
                  : null
                }
                {
                  headers.map(function(cell: any, cellIndex: number) {
                    return (
                      <TableCell key={`csv-header-cell-${cellIndex}`}>{cell.title}</TableCell>
                    )
                  })
                }
              </TableRow>
            </TableHead>
            <TableBody>
              {
                csvContent && csvContent.map(function(columns: string[], lineIndex: number) {
                  if (hasHeader && lineIndex === 0) { return null; }
                  return (
                    <TableRow key={`csv-row-${lineIndex}`}>
                      {addAddressColumns ?
                        <TableCell sx={{ fontWeight: 'bold' }} key={`csv-address-line-${lineIndex}`}>
                          {
                            headerColumnsSelection.selected.map(function(header: any) {
                              return columns[header.id];
                            }).join(", ")
                          }
                        </TableCell>
                        : null
                      }
                      {
                        columns && columns.map(function(cell: string, cellIndex: number) {
                          return (
                            <TableCell key={`csv-line-${lineIndex}-cell-${cellIndex}`}>{cell}</TableCell>
                          )
                        })
                      }
                    </TableRow>
                  )
                })
              }
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    )
  }

  const getUsageColor = function(usage: any) {
    if (!usage) {
      return "gray";
    }

    const { total, limit } = usage;
    if (total === undefined || total < (limit / 2)) {
      return "green";
    } else if (total < (limit * 0.75)) {
      return "orange";
    } else {
      return "red";
    }
  }

  const renderUsage = function() {
    if (usageRequestLoading) {
      return (
        <CircularProgress size="15px"/>
      );
    } else {
      return (
        <span>
          <span style={{color: getUsageColor(usage)}}>{usage ? `${usage.total || 0}` : `-`}</span>
          <span style={{color: "#1976d2"}}>/{usage ? `${usage.limit}` : `-`}</span>
        </span>
      );
    }
  }

  // TODO: title translation

  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  return (
    <Box sx={{ backgroundColor: "white", minHeight: "100%", display: "flex", background: "linear-gradient(#fbfeff, #f5f6ff)" }}>
      <CssBaseline />
      <AppBar
        title={t("applicationMenu.menuItem.addressBulk")}
        position="fixed"
        sx={{ 
          zIndex: (theme: any) => theme.zIndex.drawer + 1,
        }}
      />
      {!drawerOpen && (
        <Fab
          color="primary"
          aria-label={t('addressBulk.toggleFilesPanel')}
          onClick={handleDrawerToggle}
          sx={{
            position: 'fixed',
            right: 16,
            top: 80,
            zIndex: (theme: any) => theme.zIndex.drawer + 1,
          }}
        >
          <FolderIcon />
        </Fab>
      )}
      <Drawer
        variant="temporary"
        anchor="right"
        open={drawerOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            marginTop: '64px', // Height of AppBar
            height: 'calc(100% - 64px)',
            overflowX: 'hidden',
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', padding: 1, justifyContent: 'space-between' }}>
          <Typography variant="subtitle1" sx={{ paddingLeft: 2, flex: 1, fontSize: '0.875rem' }}>
            {t('addressBulk.processedFilesTitle')}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {filesLoading && (
              <CircularProgress size={16} sx={{ color: 'text.secondary' }} />
            )}
            <IconButton onClick={handleDrawerToggle} size="small">
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
        <Divider />
        <Box sx={{ overflow: 'auto', flex: 1 }}>
          {filesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', padding: 3 }}>
              <CircularProgress />
            </Box>
          ) : processedFiles.length === 0 ? (
            <Box sx={{ padding: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                {t('addressBulk.noProcessedFiles')}
              </Typography>
            </Box>
          ) : (
            <List>
              {processedFiles.map((file: any, index: number) => {
                const fileDateLabel = file.date
                  ? moment(file.date).format('DD/MM/YYYY HH:mm')
                  : file.createdAt
                  ? moment(file.createdAt).format('DD/MM/YYYY HH:mm')
                  : null;
                return (
                <MuiListItem key={index} disablePadding>
                  <ListItemButton
                    onClick={() => {
                      if (file.url) {
                        handleDownloadFile(file.url, file.originalFileName || file.name || file.fileName || `file_${index + 1}`);
                      }
                    }}
                  >
                    <InsertDriveFileIcon sx={{ marginRight: 1, color: 'text.secondary' }} />
                    <ListItemText
                      primary={file.originalFileName || file.name || file.fileName || `Fichier ${index + 1}`}
                      secondary={
                        file.bundle
                          ? (
                            <>
                              {t('addressBulk.bundleFileSubtitle')}
                              {fileDateLabel ? <><br />{fileDateLabel}</> : null}
                            </>
                          )
                          : fileDateLabel
                      }
                      sx={{ flex: 1 }}
                      primaryTypographyProps={{ fontSize: '0.875rem' }}
                      secondaryTypographyProps={{ fontSize: '0.75rem' }}
                    />
                    {file.url && (
                      <IconButton
                        edge="end"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownloadFile(file.url, file.originalFileName || file.name || file.fileName || `file_${index + 1}`);
                        }}
                        sx={{ marginLeft: 1 }}
                        size="small"
                      >
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    )}
                  </ListItemButton>
                </MuiListItem>
                );
              })}
            </List>
          )}
        </Box>
      </Drawer>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          // width: 400,
          bgcolor: 'background.paper',
          border: '2px solid #000',
          boxShadow: 24,
          p: 4,
        }}>
          <p>1/ {t('addressBulk.help.fileContainsHeader')}</p>
          <p>2/ {t('addressBulk.help.selectHeaderColumnsThatFormsTheAddress')}</p>
          <p>3/ {t('addressBulk.help.example')}</p>
          <Box sx={{ marginY: 2 }}>
            {renderCSVFile([
              ["130 rue lourmel", "75015", "Paris", "123465"],
              ["Place de la République" , "Paris", "France", "123465"],
              ["144 Rue de Vermand", "02100",  "Saint-Quentin", "123465"],
              ["28 Rue Montaigne", "43687", "Strasbourg", "123465"]
            ], [{title: "voie"}, {title: "code postal"}, {title: "ville"}, {title: "données personnalisées"}], false)}
          </Box>
          <p>{t('addressBulk.help.inThisExample')}</p>
        </Box>
      </Modal>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          paddingLeft: 3,
          paddingRight: 3,
          paddingTop: 10,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            height: "100px",
            gap: 2,
            minWidth: "1000px",
          }}>
          <Paper sx={{height: "100%", display: "flex", flexDirection: "column", alignItems: "center", paddingY: 1, paddingX: 2}}>
            <Typography sx={{fontSize: "0.875rem"}}>{t('addressBulk.currentUsageLabel')}</Typography>
            <Typography style={{flex: 1, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.5rem", fontWeight: "bold"}}>{renderUsage()}</Typography>
            <Typography sx={{fontSize: "0.875rem"}}> {t('addressBulk.currentUsagePeriodLabel', {period: moment().format('MMMM')})}</Typography>
          </Paper>
          <Paper sx={{height: "100%", display: "flex", flexDirection: "column", paddingY: 1, paddingX: 2, alignItems: "center"}}>
            <Typography sx={{fontSize: "0.875rem"}}>{t('addressBulk.yearUsageLabel')}</Typography>
            <Typography style={{flex: 1, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.5rem", fontWeight: "bold", color: "#1976d2"}}>{usage ? `${usage.perMonth.total || 0}` : `-`}</Typography>
            <Typography sx={{fontSize: "0.875rem"}}>{t('addressBulk.yearUsagePeriodLabel', {year: moment().format('YYYY')})}</Typography>
          </Paper>
          {usage && usage.perMonth.perMonth.length > 0 ? <Paper sx={{flex: 1, height: "100%", alignItems: "center", position: "relative", maxWidth: "600px"}}>
            <Typography sx={{position: "absolute", top: 8, left: 0, right: 0, textAlign: "center", fontSize: "0.875rem"}}>{t('addressBulk.historyUsageLabel')}</Typography>
            <LineChart
              xAxis={chartData.xAxis}
              series={chartData.series}
              height={140}
              sx={{
                marginTop: "-40px", 
                '& .MuiLineElement-root': {stroke: "#1976d2"}, 
                '& .MuiMarkElement-root': {stroke: "#1976d2", scale: "0.5", fill: "#1976d2"},
                '& .MuiMarkElement-highlighted': {stroke: "#1976d2", fill: "#1976d2"},
                '& .MuiAreaElement-series-usage': {
                  fill: "url('#myGradient')"
                },
                '& .MuiChartsAxis-tick': {
                  display: "none"
                }
              }}
            >
              <defs>
                <linearGradient id="myGradient" gradientTransform="rotate(90)">
                  <stop offset="5%" stopColor="rgb(120 188 255)" />
                  <stop offset="95%" stopColor="rgb(205 230 255)" />
                </linearGradient>
              </defs>
            </LineChart>
          </Paper> : null}
          <Button onClick={handleOpen}><HelpIcon/></Button>
        </Box>
        <Box
          sx={{
            paddingTop: 2,
            paddingLeft: 3,
            paddingRight: 3,
          }}
        >
          <Stepper nonLinear activeStep={activeStep} orientation="vertical">
            <Step key="file">
              <StepButton onClick={handleStep(0)}>
                <Typography>{t("addressBulk.uploadAddressFileLabel")}</Typography>
              </StepButton>
              <StepContent>
                <Typography
                  sx={{
                    marginTop: 1,
                    marginBottom: 1,
                  }}
                >
                  {t("addressBulk.fileSelectionDescription")}
                </Typography>
                <Box
                  sx={{
                  }}
                >
                  <Button
                    component="label"
                    role={undefined}
                    variant="contained"
                    tabIndex={-1}
                    startIcon={<CloudUploadIcon />}
                    sx={{
                      backgroundColor: "#304770",
                    }}
                  >
                    {t('addressBulk.selectAddressFileLabel')}
                    <VisuallyHiddenInput
                      type="file"
                      accept=".csv,.xlsx,.xls"
                      onChange={handleEventFileChange}
                      multiple={false}
                      ref={fileInputRef}
                    />
                  </Button>
                  <Stack
                    sx={{
                      marginTop: 2,
                      marginBottom: 2,
                    }}
                  >
                    <span>
                      <span>{t('addressBulk.fileNameLabel')}: </span>
                      <span>{file && file.name}</span>
                    </span>
                    <span>
                      <span>{t('addressBulk.fileSize')}: </span>
                      <span>{file && filesize(file.size)}</span>
                    </span>
                  </Stack>
                  {/* <Box>
                    <Button type="submit">{t('send')}</Button>
                  </Box> */}
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Button
                    variant="contained"
                    onClick={handleNext}
                    sx={{ mt: 1, mr: 1 }}
                  >
                    {t("continue")}
                  </Button>
                  {/* <Button
                    disabled={0 === 0}
                    onClick={handleBack}
                    sx={{ mt: 1, mr: 1 }}
                  >
                    Back
                  </Button> */}
                </Box>
              </StepContent>
            </Step>

            <Step key="columnSelection">
              <StepButton onClick={handleStep(1)}>
                <Typography>
                  <span>{t("addressBulk.columnSelectionLabel")} </span>
                  {file && inputCSVFileIsValidationErrors.length ? <span>({`${inputCSVFileIsValidationErrors.length} ${t('error(s)')}`})</span> : null}
                </Typography>
              </StepButton>
              <StepContent>
                <Box>
                  {t('addressBulk.hasHeaderLabel')} :
                  <Checkbox checked={hasHeader} onChange={(event) => { setHasHeader(event.target.checked) }}/>
                </Box>
                <Box
                  sx={{
                    display: "flex"
                  }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      padding: 1
                    }}
                  >
                    <p>{t('addressBulk.selectedHeaderColumnsLabel')} :</p>
                    <Paper
                      sx={{
                        flex: 1,
                        display: 'flex',
                        justifyContent: 'center',
                        flexWrap: 'wrap',
                        listStyle: 'none',
                        flexGrow: 0,
                      }}
                      component="ul"
                    >
                      {headerColumnsSelection.available.map((header: any, index: number) => {
                            return (
                              <ListItem key={`headerchip-${index}`}>
                                <Chip
                                  label={header.title}
                                  onClick={handleHeaderAdd(header)}
                                  component="span"
                                />
                              </ListItem>
                            );
                      })}
                    </Paper>
                  </Box>
                  <Box
                    sx={{
                      flex: 1,
                      padding: 1
                    }}
                  >
                    <p>{t('addressBulk.availableHeaderColumnsLabel')} :</p>
                    { headerColumnsSelection.selected.length ? <Paper
                      sx={{
                        flex: 1,
                        display: 'flex',
                        justifyContent: 'center',
                        flexWrap: 'wrap',
                        listStyle: 'none',
                        flexGrow: 0,
                      }}
                      component="ul"
                    >
                      {headerColumnsSelection.selected.map((header: any, index: number) => {
                            return (
                              <ListItem key={`headerchip-${index}`}>
                                <Chip
                                  label={<ChipContent params={header}/>}
                                  onDelete={handleHeaderDelete(header)}
                                />
                              </ListItem>
                            );
                      })}
                    </Paper> : null}
                  </Box>
                </Box>
                <Box
                  sx={{
                    p: 3,
                    color: "red",
                  }}
                >
                  {inputCSVFileIsValidationErrors?.map((error) => {
                    if (error.reason === "addressBulk.inputFileValidation.rowCountIsGreaterThanUsage") {
                      return (
                        <p key={`error-${error.reason}`}>
                          <Trans
                            i18nKey={error.reason}
                            components={{
                              1: <Link href={`mailto:${SUPPORT_EMAIL}`} />,
                            }}
                          />
                        </p>
                      )
                    }
                    return (
                      <p key={`error-${error.reason}`}>{t(error.reason)}</p>
                    );
                  })}
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Button
                    variant="contained"
                    onClick={handleNext}
                    sx={{ mt: 1, mr: 1 }}
                    disabled={inputCSVFileIsValidationErrors?.length !== 0}
                  >
                    {t("continue")}
                  </Button>
                  <Button
                    onClick={handleBack}
                    sx={{ mt: 1, mr: 1 }}
                  >
                    {t('back')}
                  </Button>
                </Box>
              </StepContent>
            </Step>

            <Step key="preview">
              <StepButton onClick={handleStep(2)}>
                <Typography>
                  <span>{t("addressBulk.previewLabel")} </span>
                </Typography>
              </StepButton>
              <StepContent>
                <p style={{ whiteSpace: 'pre-line' }}>{t('addressBulk.previewDescription')}</p>
                <Box
                  sx={{
                    p: 3
                  }}
                >
                  {/* {t('addressBulk.fieldSeparatorLabel')} :
                  <select value={fieldSeparator} onChange={(event) => { setFieldSeparator(event.target.value); }}>
                    <option value=",">, ({t('addressBulk.comma')})</option>
                    <option value=";">; ({t('addressBulk.semicolon')})</option>
                  </select> */}
                  {/* {t('addressBulk.lineSeparatorLabel')} :
                  <select value={lineSeparator} onChange={(event) => { setLineSeparator(event.target.value); }}>
                    <option value={"\r\n"}>{t('addressBulk.windows')}</option>
                    <option value={"\n"}>{t('addressBulk.mac')}</option>
                    <option value={"\r"}>{t('addressBulk.linux')}</option>
                  </select> */}
                  {renderCSVFile(csvContent, headers)}
                </Box>
                <Button
                    variant="contained"
                    onClick={handleNext}
                    sx={{ mt: 1, mr: 1 }}
                  >
                    {t("continue")}
                  </Button>
                  <Button
                    onClick={handleBack}
                    sx={{ mt: 1, mr: 1 }}
                  >
                    {t('back')}
                  </Button>
              </StepContent>
            </Step>

            <Step key="generate">
              <StepButton onClick={handleStep(3)}>
                <Typography>{t("addressBulk.reportGenerationLabel")}</Typography>
              </StepButton>
              <StepContent>
              <Box
                sx={{
                  flexGrow: 1,
                  p: 3,
                  display: "flex",
                  flexDirection: "row",
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    p: 0.5,
                    m: 1,
                  }}
                >
                  <p
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                    }}
                  >
                    <span style={{flex: 1}}>{t('addressBulk.availableColumnsLabel')}:</span>
                    <Button onClick={handleSelectAll}>{t('addressBulk.selectAll')}</Button>
                  </p>
                  {
                    columnsSelection.available.length
                    ? <Paper
                      sx={{
                        flex: 1,
                        display: 'flex',
                        justifyContent: 'center',
                        flexWrap: 'wrap',
                        listStyle: 'none',
                        flexGrow: 0,
                      }}
                      component="ul"
                    >
                      {columnsSelection.available.map((data: any) => {
                        return (
                          <ListItem key={data.id}>
                            <Chip
                              label={`${data.title}${data.unit ? `${data.distance}${data.unit}` : ''}`}
                              onClick={handleAdd(data)}
                              component="span"
                            />
                          </ListItem>
                        );
                      })}
                    </Paper>
                    : null
                  }
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    p: 0.5,
                    m: 1,
                  }}
                >
                  <p
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                    }}
                  >
                    <span style={{flex: 1}}>{t('addressBulk.selectedColumnsLabel')}:</span>
                    <Button onClick={handleDeselectAll}>{t('addressBulk.deselectAll')}</Button>
                  </p>
                  {
                    columnsSelection.selected.length
                    ? <Paper
                      sx={{
                        flex: 1,
                        display: 'flex',
                        justifyContent: 'center',
                        flexWrap: 'wrap',
                        listStyle: 'none',
                        flexGrow: 0,
                      }}
                      component="ul"
                    >
                      {columnsSelection.selected.map((data: any) => {
                        return (
                          <ListItem key={data.id}>
                            <Chip
                              label={<ChipContent params={data} onchange={handleDistanceChange(data)} />}
                              onDelete={handleDelete(data)}
                            />
                          </ListItem>
                        );
                      })}
                    </Paper>
                    : null
                  }
                </Box>
              </Box>
                <Box sx={{ mb: 2 }}>
                  <Tooltip
                    title={t("addressBulk.generatePdfTeasing")}
                    arrow
                    disableHoverListener={canGenerateBulkPdf}
                    disableFocusListener={canGenerateBulkPdf}
                    disableTouchListener={canGenerateBulkPdf}
                  >
                    <span style={{ display: "inline-flex" }}>
                      <FormControlLabel
                        disabled={!canGenerateBulkPdf || pdfFileExceedsCap}
                        control={
                          <Checkbox
                            checked={canGenerateBulkPdf && generatePdf && !pdfFileExceedsCap}
                            onChange={(event) => setGeneratePdf(event.target.checked)}
                            sx={{
                              pt: 0.5,
                              color: canGenerateBulkPdf ? undefined : "grey",
                              "&.Mui-disabled": { color: canGenerateBulkPdf ? undefined : "grey" },
                            }}
                          />
                        }
                        label={
                          <Box>
                            <span>{t("addressBulk.generatePdfLabel")}</span>
                            {canGenerateBulkPdf && pdfMaxRows != null ? (
                              <Typography variant="caption" color="text.secondary" display="block">
                                {t("addressBulk.generatePdfLimit", { count: pdfMaxRows })}
                              </Typography>
                            ) : null}
                            {pdfFileExceedsCap ? (
                              <Typography variant="caption" color="warning.main" display="block">
                                {t("addressBulk.generatePdfFileTooLarge", { count: addressRowCount, max: pdfMaxRows })}
                              </Typography>
                            ) : null}
                          </Box>
                        }
                        sx={{
                          alignItems: "flex-start",
                          color: canGenerateBulkPdf ? "text.primary" : "grey",
                          "&.Mui-disabled": { color: canGenerateBulkPdf ? "text.primary" : "grey" },
                        }}
                      />
                    </span>
                  </Tooltip>
                  <Box>
                  <Button
                    variant="contained"
                    onClick={handleEventFileSubmit}
                    sx={{ mt: 1, mr: 1, minWidth: 200 }}
                    disabled={inputCSVFileIsValidationErrors?.length !== 0 || requestLoading}
                  >
                    {requestLoading ? <CircularProgress size={24} sx={{ color: "white" }}/> : t('addressBulk.generateReportLabel')}
                  </Button>
                  <Button
                    onClick={handleBack}
                    sx={{ mt: 1, mr: 1 }}
                  >
                    {t('back')}
                  </Button>
                  </Box>
                </Box>
              </StepContent>
            </Step>
          </Stepper>
          {/* {activeStep === steps.length && (
            <Paper square elevation={0} sx={{ p: 3 }}>
              <Typography>All steps completed - you&apos;re finished</Typography>
              <Button onClick={handleReset} sx={{ mt: 1, mr: 1 }}>
                Reset
              </Button>
            </Paper>
          )} */}
        </Box>
      </Box>


      {/* <Box
        component="main"
        sx={{
          flexGrow: 1,
          paddingLeft: 3,
          paddingRight: 3,
          paddingTop: 4,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <DataGridComponent
          rows={reportDataParameters}
          columns={columns}
          textNorows={t("noPeril")}
        />
      </Box> */}
      {/* <Box
        sx={{
          flexGrow: 1,
          p: 3,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <ButtonConfirm
          onClick={handleEventFileSubmit}
          title={t('addressBulk.generateReportLabel')}
          loading={requestLoading}
          size="250px"
        />
      </Box> */}
    </Box>
  )
}